import React, { useState, useEffect } from 'react';
import { getProjects, createProject, deleteProject, getPMList, getAllProjectsAlerts, getAllDependencyAlerts } from '../api';
import ConfirmDialog from '../components/ConfirmDialog';
import useConfirm from '../hooks/useConfirm';

const ProjectList = ({ onSelectProject }) => {
  const { confirm, confirmDialogProps } = useConfirm();
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [showArchived, setShowArchived] = useState(false);
  const [projectAlerts, setProjectAlerts] = useState({});
  const [depAlerts, setDepAlerts] = useState({});
  const [viewMode, setViewMode] = useState(() => localStorage.getItem('projectViewMode') || 'tiles');
  const [projectNumberError, setProjectNumberError] = useState('');
  const [pmList, setPMList] = useState([]);
  const [formData, setFormData] = useState({
    project_name: '',
    project_number: '',
    client_name: '',
    original_contract_value: '',
    start_date: '',
    end_date: '',
    project_manager: '',
    billing_frequency: 'Monthly',
    default_retainage_percent: '',
    description: '',
    client_visible: true,
  });

  useEffect(() => {
    fetchProjects();
    getPMList().then(setPMList).catch(console.error);
    getAllProjectsAlerts().then(setProjectAlerts).catch(console.error);
    getAllDependencyAlerts().then(setDepAlerts).catch(console.error);
  }, []);

  const fetchProjects = async () => {
    try {
      setLoading(true);
      const data = await getProjects();
      setProjects(data);
      setError(null);
    } catch (err) {
      setError('Failed to load projects');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  // Project number auto-formatter: XXXXX-X-XX (5 chars, hyphen, 1 letter, hyphen, 2 digits)
  const formatProjectNumber = (value) => {
    // Remove all non-alphanumeric characters
    const cleaned = value.replace(/[^A-Za-z0-9]/g, '').toUpperCase();

    // Build the formatted string: XXXXX-X-XX
    let formatted = '';
    for (let i = 0; i < Math.min(cleaned.length, 8); i++) {
      if (i === 5) formatted += '-';
      if (i === 6) formatted += '-';
      formatted += cleaned[i];
    }

    return formatted;
  };

  // Validate project number format: XXXXX-X-XX
  const isValidProjectNumber = (value) => {
    const pattern = /^[A-Z]{5}-[A-Z]{1}-\d{2}$/;
    return pattern.test(value);
  };

  const handleProjectNumberChange = (e) => {
    let { value } = e.target;
    // Auto-format as user types
    const formatted = formatProjectNumber(value);
    setFormData((prev) => ({ ...prev, project_number: formatted }));

    // Validate format (but don't show error for partial input)
    if (formatted.length === 10) {
      if (!isValidProjectNumber(formatted)) {
        setProjectNumberError('Format must be XXXXX-X-XX (e.g., ALLEN-D-01)');
      } else {
        setProjectNumberError('');
      }
    } else {
      setProjectNumberError('');
    }
  };

  // Currency input: strips non-numeric chars, stores raw number, displays formatted
  const handleCurrencyChange = (e) => {
    const { name, value } = e.target;
    // Strip everything except digits and decimal point
    const raw = value.replace(/[^0-9.]/g, '');
    // Prevent multiple decimal points
    const parts = raw.split('.');
    const cleaned = parts[0] + (parts.length > 1 ? '.' + parts.slice(1).join('') : '');
    setFormData((prev) => ({ ...prev, [name]: cleaned }));
  };

  const formatCurrencyInput = (value) => {
    if (!value || value === '') return '';
    const num = parseFloat(value);
    if (isNaN(num)) return value;
    // Format with commas but keep the raw decimal if user is still typing
    const parts = value.split('.');
    const intPart = parseInt(parts[0] || '0', 10).toLocaleString('en-US');
    if (parts.length > 1) {
      return intPart + '.' + parts[1];
    }
    return intPart;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.project_name.trim()) {
      setError('Project name is required');
      return;
    }
    try {
      await createProject(formData);
      setFormData({
        project_name: '',
        project_number: '',
        client_name: '',
        original_contract_value: '',
        start_date: '',
        end_date: '',
        project_manager: '',
        billing_frequency: 'Monthly',
        default_retainage_percent: '',
        description: '',
        client_visible: true,
      });
      setProjectNumberError('');
      setShowModal(false);
      await fetchProjects();
      setError(null);
    } catch (err) {
      setError('Failed to create project');
      console.error(err);
    }
  };

  const handleDelete = async (projectId) => {
    const ok = await confirm({ title: 'Delete Project', message: 'Are you sure you want to delete this project? All associated data will be permanently removed.', confirmLabel: 'Delete', danger: true });
    if (ok) {
      try {
        await deleteProject(projectId);
        await fetchProjects();
        setError(null);
      } catch (err) {
        setError('Failed to delete project');
        console.error(err);
      }
    }
  };

  const formatCurrency = (value) => {
    if (!value) return '$0.00';
    const num = parseFloat(value);
    if (isNaN(num)) return '$0.00';
    return `$${num.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
  };

  // Split projects by status: Active and Archived
  const activeStatuses = ['Planning', 'Active', 'On Hold'];
  const archivedStatuses = ['Complete', 'Closed'];

  const activeProjectsList = projects.filter((p) => activeStatuses.includes(p.status));
  const archivedProjectsList = projects.filter((p) => archivedStatuses.includes(p.status));

  // Calculate aggregate stats
  const totalContractValue = projects.reduce(
    (sum, p) => sum + (parseFloat(p.revised_contract_value) || parseFloat(p.contract_amount) || 0),
    0
  );
  const totalBilled = projects.reduce(
    (sum, p) => sum + (parseFloat(p.total_billed) || 0),
    0
  );
  const totalRetainage = projects.reduce(
    (sum, p) => sum + (parseFloat(p.total_retainage) || 0),
    0
  );
  const leftToBill = totalContractValue - totalBilled;
  const activeProjectsCount = activeProjectsList.length;

  if (loading) {
    return (
      <div style={{ padding: '40px', textAlign: 'center', color: '#64748b' }}>
        Loading projects...
      </div>
    );
  }

  return (
    <div>
      {error && (
        <div className="alert alert-error" style={{ marginBottom: '16px' }}>
          {error}
        </div>
      )}

      {/* Dashboard Summary Stats */}
      <div className="stats-grid">
        <div className="stat-card">
          <div className="stat-label">Total Projects</div>
          <div className="stat-value">{projects.length}</div>
        </div>
        <div className="stat-card">
          <div className="stat-label">Active Projects</div>
          <div className="stat-value" style={{ color: '#4CAF50' }}>{activeProjectsCount}</div>
        </div>
        <div className="stat-card">
          <div className="stat-label">Total Contract Value</div>
          <div className="stat-value">{formatCurrency(totalContractValue)}</div>
        </div>
        <div className="stat-card">
          <div className="stat-label">Left to Bill</div>
          <div className="stat-value">{formatCurrency(leftToBill)}</div>
        </div>
        <div className="stat-card">
          <div className="stat-label">Total Billed</div>
          <div className="stat-value">{formatCurrency(totalBilled)}</div>
          {totalRetainage > 0 && (
            <div style={{ fontSize: 11, color: '#d97706', marginTop: 2 }}>+{formatCurrency(totalRetainage)} retainage held</div>
          )}
        </div>
      </div>

      {/* Projects Header */}
      <div className="page-header" style={{ marginTop: '24px' }}>
        <h2 style={{ margin: 0, fontSize: '18px', fontWeight: 600 }}>
          {showArchived ? 'Archived Projects' : 'Active Projects'}
        </h2>
        <button className="btn btn-primary" onClick={() => setShowModal(true)}>
          + New Project
        </button>
      </div>

      {/* Toggle between Active/Archived and view mode */}
      {projects.length > 0 && (
        <div style={{ marginBottom: '16px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <div style={{ display: 'flex', gap: '8px' }}>
            <button
              className={`btn ${!showArchived ? 'btn-primary' : 'btn-secondary'}`}
              onClick={() => setShowArchived(false)}
              style={{ padding: '8px 16px', fontSize: '14px' }}
            >
              Active Projects ({activeProjectsList.length})
            </button>
            <button
              className={`btn ${showArchived ? 'btn-primary' : 'btn-secondary'}`}
              onClick={() => setShowArchived(true)}
              style={{ padding: '8px 16px', fontSize: '14px' }}
            >
              Archived Projects ({archivedProjectsList.length})
            </button>
          </div>
          <div className="view-toggle">
            <button
              className={`view-toggle-btn ${viewMode === 'tiles' ? 'active' : ''}`}
              onClick={() => { setViewMode('tiles'); localStorage.setItem('projectViewMode', 'tiles'); }}
              title="Tile view"
            >
              <svg width="16" height="16" viewBox="0 0 16 16" fill="currentColor"><rect x="1" y="1" width="6" height="6" rx="1"/><rect x="9" y="1" width="6" height="6" rx="1"/><rect x="1" y="9" width="6" height="6" rx="1"/><rect x="9" y="9" width="6" height="6" rx="1"/></svg>
            </button>
            <button
              className={`view-toggle-btn ${viewMode === 'list' ? 'active' : ''}`}
              onClick={() => { setViewMode('list'); localStorage.setItem('projectViewMode', 'list'); }}
              title="List view"
            >
              <svg width="16" height="16" viewBox="0 0 16 16" fill="currentColor"><rect x="1" y="2" width="14" height="2.5" rx="0.5"/><rect x="1" y="6.75" width="14" height="2.5" rx="0.5"/><rect x="1" y="11.5" width="14" height="2.5" rx="0.5"/></svg>
            </button>
          </div>
        </div>
      )}

      {/* Project Cards / List */}
      {projects.length === 0 ? (
        <div className="empty-state" style={{ textAlign: 'center', padding: '60px 20px' }}>
          <p style={{ color: '#64748b', fontSize: '16px', marginBottom: '16px' }}>
            No projects yet. Create your first project to get started.
          </p>
          <button className="btn btn-primary" onClick={() => setShowModal(true)}>
            Create First Project
          </button>
        </div>
      ) : (showArchived ? archivedProjectsList : activeProjectsList).length === 0 ? (
        <div className="empty-state" style={{ textAlign: 'center', padding: '60px 20px' }}>
          <p style={{ color: '#64748b', fontSize: '16px', marginBottom: '16px' }}>
            No {showArchived ? 'archived' : 'active'} projects found.
          </p>
        </div>
      ) : viewMode === 'tiles' ? (
        <div className="projects-grid">
          {(showArchived ? archivedProjectsList : activeProjectsList).map((project) => {
            const contractVal = parseFloat(project.revised_contract_value) || parseFloat(project.contract_amount) || 0;
            const billedVal = parseFloat(project.total_billed) || 0;
            const projRetainage = parseFloat(project.total_retainage) || 0;
            const billedPercent = contractVal > 0 ? ((billedVal / contractVal) * 100).toFixed(1) : 0;

            return (
              <div
                key={project.id}
                className="card project-card"
                onClick={() => onSelectProject(project.id)}
              >
                <div className="project-card-header">
                  <div style={{ display: 'flex', alignItems: 'center', gap: 12, flex: 1, minWidth: 0 }}>
                    {project.logo_url ? (
                      <img src={project.logo_url} alt="" style={{
                        width: 40, height: 40, borderRadius: 8, objectFit: 'cover',
                        border: '1px solid #e2e8f0', flexShrink: 0,
                      }} />
                    ) : (
                      <div style={{
                        width: 40, height: 40, borderRadius: 8, background: '#f1f5f9',
                        border: '1px solid #e2e8f0', display: 'flex', alignItems: 'center',
                        justifyContent: 'center', flexShrink: 0, fontSize: 16, fontWeight: 700,
                        color: '#94a3b8',
                      }}>
                        {(project.project_name || '?')[0].toUpperCase()}
                      </div>
                    )}
                    <div style={{ minWidth: 0 }}>
                      <h3 className="project-card-title" style={{ margin: 0 }}>{project.project_name}</h3>
                      <span className="project-card-number">
                        {project.project_number ? `#${project.project_number}` : ''}
                      </span>
                    </div>
                  </div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                    <span
                      className={`badge ${
                        project.status === 'Active'
                          ? 'badge-labor'
                          : project.status === 'Complete'
                          ? 'badge-material'
                          : 'badge-equipment'
                      }`}
                    >
                      {project.status || 'Active'}
                    </span>
                    <button
                      className="btn btn-small btn-danger"
                      onClick={(e) => {
                        e.stopPropagation();
                        handleDelete(project.id);
                      }}
                      title="Delete project"
                    >
                      Delete
                    </button>
                  </div>
                </div>

                <div className="project-card-body">
                  <div className="project-card-info">
                    <div className="project-card-info-item">
                      <span className="project-card-info-label">Client</span>
                      <span className="project-card-info-value">{project.client_name || '—'}</span>
                    </div>
                    <div className="project-card-info-item">
                      <span className="project-card-info-label">Manager{project.project_manager && project.project_manager.includes(',') ? 's' : ''}</span>
                      <span className="project-card-info-value">{project.project_manager || '—'}</span>
                    </div>
                    <div className="project-card-info-item">
                      <span className="project-card-info-label">Contract Value</span>
                      <span className="project-card-info-value">{formatCurrency(contractVal)}</span>
                    </div>
                    <div className="project-card-info-item">
                      <span className="project-card-info-label">Billed</span>
                      <span className="project-card-info-value">
                        {formatCurrency(billedVal)}
                        {projRetainage > 0 && (
                          <span style={{ display: 'block', fontSize: 10, color: '#d97706', fontWeight: 400 }}>+{formatCurrency(projRetainage)} ret.</span>
                        )}
                      </span>
                    </div>
                  </div>

                  {/* Progress Bar */}
                  <div style={{ marginTop: '12px' }}>
                    <div
                      style={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        fontSize: '12px',
                        color: '#64748b',
                        marginBottom: '4px',
                      }}
                    >
                      <span>Billed Progress</span>
                      <span>{billedPercent}%</span>
                    </div>
                    <div className="progress-bar">
                      <div
                        className="progress-bar-fill"
                        style={{
                          width: `${Math.min(billedPercent, 100)}%`,
                          backgroundColor:
                            billedPercent >= 100
                              ? '#2196f3'
                              : billedPercent >= 75
                              ? '#4CAF50'
                              : billedPercent >= 50
                              ? '#ff9800'
                              : '#2563eb',
                        }}
                      />
                    </div>
                  </div>

                  {/* Progress Alerts */}
                  {projectAlerts[String(project.id)] && (
                    <div style={{
                      marginTop: '10px', padding: '6px 10px', borderRadius: '6px',
                      fontSize: '12px', fontWeight: 500, display: 'flex', alignItems: 'center', gap: '6px',
                      backgroundColor: projectAlerts[String(project.id)].critical_count > 0 ? '#fef2f2' : '#fffbeb',
                      color: projectAlerts[String(project.id)].critical_count > 0 ? '#dc2626' : '#d97706',
                      border: `1px solid ${projectAlerts[String(project.id)].critical_count > 0 ? '#fecaca' : '#fde68a'}`,
                    }}>
                      <span style={{ fontSize: '14px' }}>
                        {projectAlerts[String(project.id)].critical_count > 0 ? '\u26A0' : '\u25CF'}
                      </span>
                      <span>
                        {projectAlerts[String(project.id)].alert_count} task{projectAlerts[String(project.id)].alert_count !== 1 ? 's' : ''} behind schedule
                        {projectAlerts[String(project.id)].critical_count > 0 && (
                          <span style={{ fontWeight: 600 }}> ({projectAlerts[String(project.id)].critical_count} critical)</span>
                        )}
                      </span>
                    </div>
                  )}
                  {/* Dependency / Scheduling Alerts */}
                  {depAlerts[String(project.id)] && (
                    <div style={{
                      marginTop: projectAlerts[String(project.id)] ? '6px' : '10px',
                      padding: '6px 10px', borderRadius: '6px',
                      fontSize: '12px', fontWeight: 500, display: 'flex', alignItems: 'center', gap: '6px',
                      backgroundColor: depAlerts[String(project.id)].critical_count > 0 ? '#fef2f2' : '#fffbeb',
                      color: depAlerts[String(project.id)].critical_count > 0 ? '#dc2626' : '#d97706',
                      border: `1px solid ${depAlerts[String(project.id)].critical_count > 0 ? '#fecaca' : '#fde68a'}`,
                    }}>
                      <span style={{ fontSize: '14px' }}>{'\u26A0'}</span>
                      <span>
                        {depAlerts[String(project.id)].alert_count} scheduling issue{depAlerts[String(project.id)].alert_count !== 1 ? 's' : ''}
                        {depAlerts[String(project.id)].critical_count > 0 && (
                          <span style={{ fontWeight: 600 }}> ({depAlerts[String(project.id)].critical_count} critical)</span>
                        )}
                      </span>
                    </div>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      ) : (
        /* List View */
        <div className="projects-list" style={{ marginTop: '16px' }}>
          <div className="projects-list-header">
            <span style={{ flex: 2 }}>Project</span>
            <span style={{ flex: 1 }}>Client</span>
            <span style={{ flex: 1 }}>Manager(s)</span>
            <span style={{ flex: 1, textAlign: 'right' }}>Contract Value</span>
            <span style={{ flex: 1, textAlign: 'right' }}>Billed</span>
            <span style={{ width: 100, textAlign: 'center' }}>Progress</span>
            <span style={{ width: 140, textAlign: 'center' }}>Alerts</span>
            <span style={{ width: 80, textAlign: 'center' }}>Status</span>
            <span style={{ width: 60 }}></span>
          </div>
          {(showArchived ? archivedProjectsList : activeProjectsList).map((project) => {
            const contractVal = parseFloat(project.revised_contract_value) || parseFloat(project.contract_amount) || 0;
            const billedVal = parseFloat(project.total_billed) || 0;
            const projRetainage = parseFloat(project.total_retainage) || 0;
            const billedPercent = contractVal > 0 ? ((billedVal / contractVal) * 100).toFixed(1) : 0;

            return (
              <div
                key={project.id}
                className="projects-list-row"
                onClick={() => onSelectProject(project.id)}
              >
                <span style={{ flex: 2, display: 'flex', alignItems: 'center', gap: 10 }}>
                  {project.logo_url ? (
                    <img src={project.logo_url} alt="" style={{
                      width: 32, height: 32, borderRadius: 6, objectFit: 'cover',
                      border: '1px solid #e2e8f0', flexShrink: 0,
                    }} />
                  ) : (
                    <div style={{
                      width: 32, height: 32, borderRadius: 6, background: '#f1f5f9',
                      border: '1px solid #e2e8f0', display: 'flex', alignItems: 'center',
                      justifyContent: 'center', flexShrink: 0, fontSize: 13, fontWeight: 700,
                      color: '#94a3b8',
                    }}>
                      {(project.project_name || '?')[0].toUpperCase()}
                    </div>
                  )}
                  <span>
                    <span className="list-project-name" style={{ fontWeight: 600, display: 'block', fontSize: 14 }}>
                      {project.project_name}
                    </span>
                    {project.project_number && (
                      <span style={{ fontSize: 11, color: '#94a3b8', fontFamily: 'monospace' }}>
                        #{project.project_number}
                      </span>
                    )}
                  </span>
                </span>
                <span className="list-secondary-text" style={{ flex: 1, fontSize: 13 }}>{project.client_name || '—'}</span>
                <span className="list-secondary-text" style={{ flex: 1, fontSize: 13 }}>{project.project_manager || '—'}</span>
                <span className="list-primary-text" style={{ flex: 1, textAlign: 'right', fontWeight: 600, fontSize: 13 }}>{formatCurrency(contractVal)}</span>
                <span className="list-secondary-text" style={{ flex: 1, textAlign: 'right', fontSize: 13 }}>
                  {formatCurrency(billedVal)}
                  {projRetainage > 0 && (
                    <span style={{ display: 'block', fontSize: 10, color: '#d97706' }}>+{formatCurrency(projRetainage)} ret.</span>
                  )}
                </span>
                <span style={{ width: 100, display: 'flex', alignItems: 'center', gap: 6 }}>
                  <div className="progress-bar" style={{ flex: 1, height: 6 }}>
                    <div
                      className="progress-bar-fill"
                      style={{
                        width: `${Math.min(billedPercent, 100)}%`,
                        backgroundColor:
                          billedPercent >= 100 ? '#2196f3'
                            : billedPercent >= 75 ? '#4CAF50'
                            : billedPercent >= 50 ? '#ff9800'
                            : '#2563eb',
                      }}
                    />
                  </div>
                  <span style={{ fontSize: 11, color: '#64748b', minWidth: 32, textAlign: 'right' }}>{billedPercent}%</span>
                </span>
                <span style={{ width: 140, textAlign: 'center', display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 2 }}>
                  {projectAlerts[String(project.id)] ? (
                    <span style={{
                      display: 'inline-flex', alignItems: 'center', gap: '4px',
                      padding: '2px 8px', borderRadius: '4px', fontSize: 11, fontWeight: 500,
                      backgroundColor: projectAlerts[String(project.id)].critical_count > 0 ? '#fef2f2' : '#fffbeb',
                      color: projectAlerts[String(project.id)].critical_count > 0 ? '#dc2626' : '#d97706',
                    }}>
                      {projectAlerts[String(project.id)].critical_count > 0 ? '\u26A0' : '\u25CF'}{' '}
                      {projectAlerts[String(project.id)].alert_count} behind
                    </span>
                  ) : null}
                  {depAlerts[String(project.id)] ? (
                    <span style={{
                      display: 'inline-flex', alignItems: 'center', gap: '4px',
                      padding: '2px 8px', borderRadius: '4px', fontSize: 11, fontWeight: 500,
                      backgroundColor: depAlerts[String(project.id)].critical_count > 0 ? '#fef2f2' : '#fffbeb',
                      color: depAlerts[String(project.id)].critical_count > 0 ? '#dc2626' : '#d97706',
                    }}>
                      {'\u26A0'} {depAlerts[String(project.id)].alert_count} sched
                    </span>
                  ) : null}
                  {!projectAlerts[String(project.id)] && !depAlerts[String(project.id)] && (
                    <span style={{ fontSize: 11, color: '#94a3b8' }}>—</span>
                  )}
                </span>
                <span style={{ width: 80, textAlign: 'center' }}>
                  <span
                    className={`badge ${
                      project.status === 'Active' ? 'badge-labor'
                        : project.status === 'Complete' ? 'badge-material'
                        : 'badge-equipment'
                    }`}
                    style={{ fontSize: 11, padding: '3px 8px' }}
                  >
                    {project.status || 'Active'}
                  </span>
                </span>
                <span style={{ width: 60, textAlign: 'right' }}>
                  <button
                    className="btn btn-small btn-danger"
                    onClick={(e) => {
                      e.stopPropagation();
                      handleDelete(project.id);
                    }}
                    title="Delete project"
                    style={{ fontSize: 11, padding: '3px 8px' }}
                  >
                    Delete
                  </button>
                </span>
              </div>
            );
          })}
        </div>
      )}

      {/* Create Project Modal */}
      {showModal && (
        <div className="modal-overlay" onClick={() => setShowModal(false)}>
          <div className="modal card" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>Create New Project</h2>
              <button className="btn btn-close" onClick={() => setShowModal(false)}>
                &times;
              </button>
            </div>
            <form onSubmit={handleSubmit} className="modal-body">
              <div className="form-group">
                <label htmlFor="project_name">
                  Project Name <span className="required">*</span>
                </label>
                <input
                  type="text"
                  id="project_name"
                  name="project_name"
                  value={formData.project_name}
                  onChange={handleInputChange}
                  required
                  placeholder="e.g., Downtown Office Renovation"
                />
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label htmlFor="project_number">Project Number</label>
                  <input
                    type="text"
                    id="project_number"
                    name="project_number"
                    value={formData.project_number}
                    onChange={handleProjectNumberChange}
                    placeholder="XXXXX-X-XX (e.g., ALLEN-D-01)"
                    maxLength="10"
                  />
                  {projectNumberError && (
                    <div style={{ color: '#dc2626', fontSize: '12px', marginTop: '4px' }}>
                      {projectNumberError}
                    </div>
                  )}
                </div>
                <div className="form-group">
                  <label htmlFor="client_name">Client Name</label>
                  <input
                    type="text"
                    id="client_name"
                    name="client_name"
                    value={formData.client_name}
                    onChange={handleInputChange}
                    placeholder="e.g., ABC Construction"
                  />
                </div>
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label htmlFor="original_contract_value">Original Contract Value</label>
                  <div style={{ position: 'relative' }}>
                    <span style={{
                      position: 'absolute',
                      left: '12px',
                      top: '50%',
                      transform: 'translateY(-50%)',
                      color: '#64748b',
                      fontSize: '14px',
                      pointerEvents: 'none',
                    }}>$</span>
                    <input
                      type="text"
                      id="original_contract_value"
                      name="original_contract_value"
                      value={formatCurrencyInput(formData.original_contract_value)}
                      onChange={handleCurrencyChange}
                      placeholder="0.00"
                      style={{ paddingLeft: '28px' }}
                    />
                  </div>
                </div>
                <div className="form-group">
                  <label htmlFor="project_manager">Project Manager(s)</label>
                  <div style={{
                    border: '1px solid #e2e8f0', borderRadius: '8px', padding: '8px 12px',
                    backgroundColor: '#fff', minHeight: '40px',
                  }}>
                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px', marginBottom: (formData.project_manager ? 8 : 0) }}>
                      {(formData.project_manager || '').split(',').filter(Boolean).map((pm, i) => (
                        <span key={i} style={{
                          display: 'inline-flex', alignItems: 'center', gap: '6px',
                          padding: '4px 10px', borderRadius: '16px', fontSize: '12px', fontWeight: 600,
                          backgroundColor: '#eff6ff', color: '#2563eb', border: '1px solid #bfdbfe',
                        }}>
                          {pm.trim()}
                          <span
                            onClick={() => {
                              const current = (formData.project_manager || '').split(',').filter(Boolean).map(s => s.trim());
                              const updated = current.filter((_, idx) => idx !== i);
                              setFormData({ ...formData, project_manager: updated.join(', ') });
                            }}
                            style={{ cursor: 'pointer', fontSize: '14px', lineHeight: 1, color: '#93c5fd' }}
                          >&times;</span>
                        </span>
                      ))}
                    </div>
                    <select
                      value=""
                      onChange={(e) => {
                        if (!e.target.value) return;
                        const current = (formData.project_manager || '').split(',').filter(Boolean).map(s => s.trim());
                        if (!current.includes(e.target.value)) {
                          setFormData({ ...formData, project_manager: [...current, e.target.value].join(', ') });
                        }
                      }}
                      style={{
                        width: '100%', padding: '6px 8px', fontSize: '13px', border: 'none',
                        backgroundColor: 'transparent', color: '#64748b', outline: 'none', cursor: 'pointer',
                      }}
                    >
                      <option value="">+ Add a project manager...</option>
                      {pmList
                        .filter(pm => !(formData.project_manager || '').split(',').map(s => s.trim()).includes(pm.name))
                        .map(pm => (
                          <option key={pm.id} value={pm.name}>{pm.name} ({pm.role_label || pm.role})</option>
                        ))}
                    </select>
                  </div>
                </div>
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label htmlFor="start_date">Start Date</label>
                  <input
                    type="date"
                    id="start_date"
                    name="start_date"
                    value={formData.start_date}
                    onChange={handleInputChange}
                  />
                </div>
                <div className="form-group">
                  <label htmlFor="end_date">End Date</label>
                  <input
                    type="date"
                    id="end_date"
                    name="end_date"
                    value={formData.end_date}
                    onChange={handleInputChange}
                  />
                </div>
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label htmlFor="billing_frequency">Billing Frequency</label>
                  <select
                    id="billing_frequency"
                    name="billing_frequency"
                    value={formData.billing_frequency}
                    onChange={handleInputChange}
                  >
                    <option value="Monthly">Monthly</option>
                    <option value="Quarterly">Quarterly</option>
                  </select>
                </div>
                <div className="form-group">
                  <label htmlFor="default_retainage_percent">Default Retainage %</label>
                  <input
                    type="number"
                    id="default_retainage_percent"
                    name="default_retainage_percent"
                    value={formData.default_retainage_percent}
                    onChange={handleInputChange}
                    step="0.01"
                    min="0"
                    max="100"
                    placeholder="e.g., 10"
                  />
                </div>
              </div>

              <div className="form-group">
                <label htmlFor="description">Description</label>
                <textarea
                  id="description"
                  name="description"
                  value={formData.description}
                  onChange={handleInputChange}
                  rows="3"
                  placeholder="Brief project description..."
                />
              </div>

              <div className="modal-footer">
                <button
                  type="button"
                  className="btn btn-secondary"
                  onClick={() => setShowModal(false)}
                >
                  Cancel
                </button>
                <button type="submit" className="btn btn-primary">
                  Create Project
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
      <ConfirmDialog {...confirmDialogProps} />
    </div>
  );
};

export default ProjectList;
